import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { WIDGETS, CATEGORIES } from "@/lib/widgets";
import { Search, Plus, GripVertical, X } from "lucide-react";

/**
 * The widget library panel - like an app store list. Drag a card onto the canvas
 * or press + to add it. Every card has a plain-English description.
 */
export default function WidgetLibrary({ onAdd, onDragType, onClose }) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");

  const entries = Object.entries(WIDGETS).filter(([type, meta]) => {
    if (category !== "all" && meta.category !== category) return false;
    if (query && !`${meta.name} ${meta.desc}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  return (
    <aside className="flex h-full w-[300px] shrink-0 flex-col border-l bg-card" data-testid="widget-library">
      <div className="flex items-center justify-between px-4 pb-2 pt-4">
        <div>
          <h3 className="font-display text-sm font-semibold">Widget library</h3>
          <p className="text-[11px] text-muted-foreground">Drag onto the canvas, or press +</p>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} data-testid="widget-library-close">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="px-4 pb-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search widgets…"
            className="h-8 pl-8 text-xs" data-testid="widget-library-search" />
        </div>
      </div>
      <div className="px-4 pb-2">
        <Tabs value={category} onValueChange={setCategory}>
          <TabsList className="grid h-8 w-full grid-cols-4">
            <TabsTrigger value="all" className="text-[11px]">All</TabsTrigger>
            {CATEGORIES.map((c) => (
              <TabsTrigger key={c.id} value={c.id} className="text-[11px]" data-testid={`library-tab-${c.id}`}>
                {c.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>
      <ScrollArea className="min-h-0 flex-1 px-4 pb-4">
        <div className="space-y-2 pt-1">
          {entries.map(([type, meta]) => {
            const Icon = meta.icon;
            return (
              <div
                key={type}
                draggable
                unselectable="on"
                onDragStart={(e) => {
                  e.dataTransfer.setData("text/plain", type);
                  onDragType?.(type);
                }}
                onDragEnd={() => onDragType?.(null)}
                className="group flex cursor-grab items-start gap-2.5 rounded-xl border bg-card p-3 transition-colors duration-150 hover:bg-muted/40 active:cursor-grabbing"
                data-testid={`library-widget-${type}`}
              >
                <GripVertical className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/40 group-hover:text-muted-foreground" />
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <Icon className="h-3.5 w-3.5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium">{meta.name}</div>
                  <div className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-muted-foreground">{meta.desc}</div>
                </div>
                <Button variant="secondary" size="icon" className="h-6 w-6 shrink-0 opacity-0 transition-opacity duration-150 group-hover:opacity-100"
                  onClick={() => onAdd(type)} data-testid={`library-add-${type}`}>
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
          {!entries.length && (
            <p className="py-8 text-center text-xs text-muted-foreground">No widgets match “{query}”</p>
          )}
        </div>
      </ScrollArea>
    </aside>
  );
}
